import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
  Tooltip,
  Popper,
} from "@mui/material";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import ArrowDownIcon from "@styles/all-images/icons/chevron-down.svg";
import { ReactNode, useCallback, useRef } from "react";
import ElipsedText from "../../elipsis/ElipsedText";

interface Option {
  id?: string | number;
  label?: string;
  [key: string]: any;
}

interface DynamicAutocompleteProps {
  name: string;
  label: string;
  options: Option[];
  selectedValue: number | number[] | null;
  onChange: (value: any) => void;
  onBlur?: (event: any) => void;
  error?: string;
  touched?: boolean;
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  labelKey?: string;
  valueKey?: string;
  className?: string;
  helperText?: string;
  multiple?: boolean;
  maxChips?: number;
  maxLengthMenu?: number;
  maxLengthChip?: number;
  filterOptions?: (
    options: Option[],
    state: { inputValue: string },
  ) => Option[];
  hideLabel?: boolean;
}

const DynamicAutocomplete = ({
  name,
  label,
  options,
  selectedValue,
  onChange,
  onBlur,
  error,
  touched,
  loading = false,
  required = false,
  disabled = false,
  placeholder = "Select an option",
  labelKey = "label",
  valueKey = "id",
  className = "",
  helperText = "",
  multiple = false,
  maxLengthMenu = 20,
  maxChips = 2,
  maxLengthChip = 10,
  filterOptions,
  hideLabel = false,
}: DynamicAutocompleteProps) => {
  const EscapeStrings = ["", null, undefined, "null", "undefined"];
  const customInputDivRef = useRef<HTMLDivElement>(null);

  const handleChange = (
    _: any,
    newValue: Option | Option[] | null,
    isMultiple?: boolean,
  ) => {
    console.log("newValue: ", newValue);
    if (multiple && Array.isArray(newValue)) {
      let newValueData;
      if (!isMultiple) {
        newValueData = newValue?.map((item: Option) => item.id);
      } else {
        newValueData = newValue;
      }
      console.log("newValueData: ", newValueData);
      onChange(newValueData || []);
    } else if (!Array.isArray(newValue)) {
      onChange(newValue?.id);
    }
  };

  const getCurrentValue = (): Option | Option[] | null => {
    if (multiple && Array.isArray(selectedValue)) {
      const currentValueData = selectedValue?.map((item) => {
        const option = options.find((option) => option[valueKey] === item);
        return {
          id: option?.[valueKey],
          label: option?.[labelKey],
        };
      });
      return currentValueData || [];
    }
    if (!selectedValue) return null;
    const currentValueData = options.find(
      (option) => option[valueKey] === selectedValue,
    );
    const currentValue = {
      id: currentValueData?.[valueKey],
      label: currentValueData?.[labelKey],
    };
    return currentValueData ? currentValue : null;
  };

  const getOptions: () => Option[] = useCallback(() => {
    const optionsData = options.map((option) => ({
      id: option[valueKey],
      label: option[labelKey],
    }));
    return optionsData;
  }, [options, valueKey, labelKey]);

  const renderCustomInput = (params: any) => (
    <div ref={params.InputProps.ref}>
      <div
        ref={customInputDivRef}
        className="tw-w-full tw-min-h-[44px] tw-px-2 tw-py-1 tw-border tw-border-[#D5D7DA] tw-rounded-md tw-flex tw-items-center tw-flex-wrap tw-gap-2"
      >
        {multiple &&
        Array.isArray(selectedValue) &&
        selectedValue?.length > 0 &&
        !loading ? (
          <>
            {selectedValue.slice(0, maxChips).map((item) => {
              const option = options.find(
                (option) => option[valueKey] === item,
              );
              return (
                <Tooltip
                  key={option?.[valueKey]}
                  title={option?.[labelKey]}
                  placement="top"
                >
                  <span className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-lg tw-text-sm tw-text-[#232323] tw-overflow-hidden tw-inline-flex tw-items-center">
                    {/* <span className="tw-truncate"> */}
                    <ElipsedText
                      text={option?.[labelKey]}
                      maxLength={maxLengthChip}
                    />
                    {/* </span> */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange(
                          null,
                          selectedValue.filter((v: number) => v !== item),
                          true,
                        );
                      }}
                    >
                      <img
                        src={CloseIcon}
                        alt="Remove"
                        className="tw-w-[10px] tw-h-[10px] tw-mx-2 tw-flex-shrink-0"
                      />
                    </button>
                  </span>
                </Tooltip>
              );
            })}
            {selectedValue.length > maxChips && (
              <span className="tw-bg-[#146C94] tw-text-white tw-text-sm tw-px-2 tw-py-1 tw-rounded-full tw-flex-shrink-0">
                +{selectedValue.length - maxChips}
              </span>
            )}
          </>
        ) : null}
        <input
          {...params.inputProps}
          type="text"
          placeholder={
            !selectedValue ||
            (Array.isArray(selectedValue) && selectedValue.length === 0)
              ? placeholder
              : ""
          }
          className="tw-flex-1 tw-w-[20px] tw-outline-none tw-border-none"
        />
        {loading && (
          <div className="tw-absolute tw-right-10">
            <CustomLoader
              loading={loading}
              size="20"
              isWrapper
              className="tw-flex tw-items-center"
            />
          </div>
        )}
        <img
          src={ArrowDownIcon}
          alt="Toggle"
          onClick={() => {
            if (customInputDivRef.current) {
              const inputElement =
                customInputDivRef.current?.querySelector("input");
              if (inputElement) {
                inputElement.blur();
                inputElement.focus();
              }
            }
          }}
          className={`tw-w-4 tw-h-4 tw-ml-auto tw-transform ${params.InputProps.ref && "current" in params.InputProps.ref && params.InputProps.ref.current?.getAttribute("aria-expanded") === "true" ? "tw-rotate-180" : ""}`}
        />
      </div>
    </div>
  );

  return (
    <div className={`${hideLabel ? "tw-h-[50px]" : ""}`}>
      <FormControl
        fullWidth
        error={touched && Boolean(error) && !EscapeStrings.includes(error)}
        className={`${className}`}
      >
        {!hideLabel && (
          <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
            {label}
            {required && <span className="tw-text-red-500">*</span>}
          </label>
        )}

        <Autocomplete
          multiple={multiple}
          disabled={disabled || loading}
          options={getOptions()}
          value={getCurrentValue()}
          onChange={(event, newValue) => handleChange(event, newValue)}
          onBlur={onBlur}
          openOnFocus
          filterOptions={filterOptions}
          getOptionLabel={(option: Option) => option.label || ""}
          isOptionEqualToValue={(option: Option, value: Option) => {
            const condition = option.id === value.id;
            return condition;
          }}
          renderInput={
            multiple
              ? renderCustomInput
              : (params) => (
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    error={
                      touched &&
                      Boolean(error) &&
                      !EscapeStrings.includes(error)
                    }
                    // helperText={touched && error}
                    className="tw-bg-white"
                    InputProps={{
                      ...params.InputProps,
                      className:
                        params.InputProps.className +
                        " tw-h-[40px] tw-rounded-md",
                      endAdornment: (
                        <>
                          {loading && (
                            <div className="tw-absolute tw-right-10">
                              <CustomLoader
                                loading={loading}
                                size="20"
                                isWrapper
                                className="tw-flex tw-items-center"
                              />
                            </div>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <ElipsedText
                text={option.label || ""}
                maxLength={maxLengthMenu}
              />
            </li>
          )}
          PopperComponent={(popperProps) => (
            <Popper
              {...popperProps}
              placement="bottom-start"
              modifiers={[
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    fallbackPlacements: ["top-start"],
                  },
                },
              ]}
            >
              <div className="tw-bg-white tw-border tw-border-[#E9EAEB] tw-rounded-md tw-shadow-lg">
                {popperProps.children as ReactNode}
              </div>
            </Popper>
          )}
        />
      </FormControl>

      {!hideLabel && (
        <span className="tw-opacity-0 tw-relative tw-top-[10px]">
          Error Block
        </span>
      )}
      <div className="tw-pt-1">
        {Boolean(touched && error) && !EscapeStrings.includes(error) && (
          <div className="tw-text-red-500 tw-text-sm">{error}</div>
        )}
        {Boolean(helperText) && !EscapeStrings.includes(helperText) && (
          <div className="tw-text-gray-500 tw-text-sm">{helperText}</div>
        )}
      </div>
    </div>
  );
};

export default DynamicAutocomplete;
